import React, { useEffect, useRef } from 'react'
import { Drawer as SlidingDrawer, Target, useIsOpen } from '@accessible/drawer'
import { CancelIcon } from '@chegg-ui/theme'
import { useModalBehavior } from '@chegg-ui/modal'
import * as Styled from './styled'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const DRAWER_ID = 'citation-drawer-wrapper'

const useLockScroll = (locked: boolean) => {
  const bodyOverflow = useRef<string>('')
  // We only want to reset the scroll to our previously saved value if we have over-written it
  const overWritten = useRef<boolean>(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (locked) {
        bodyOverflow.current = window.document.body.style.overflow
        overWritten.current = true
        window.document.body.style.overflow = 'hidden'
      } else if (overWritten.current) {
        window.document.body.style.overflow = bodyOverflow.current
        overWritten.current = false
      }
    }
  }, [locked])
}

const Overlay: React.FunctionComponent<{ lockScroll: boolean }> = ({ lockScroll }) => {
  const modalIsOpen = useIsOpen()
  useLockScroll(lockScroll)

  return <Styled.Overlay visible={!!modalIsOpen} />
}

const Drawer: React.FC<Props> = ({ isOpen, onClose, children }) => {
  const { modalRef, onModalKeyDown } = useModalBehavior({
    isOpen,
    onClose,
    id: DRAWER_ID,
  })
  return (
    <SlidingDrawer open={isOpen}>
      <Overlay lockScroll={isOpen} />
      <Target
        placement="right"
        openStyle={Styled.drawerStyle}
        closedStyle={Styled.drawerStyle}
      >
        <Styled.DrawerWrapper
          role="dialog"
          aria-modal
          onKeyDown={onModalKeyDown}
          ref={modalRef}
          id={DRAWER_ID}
        >
          <Styled.DrawerContent>{children}</Styled.DrawerContent>
          <Styled.CloseButton onClick={onClose} aria-label="Close">
            <CancelIcon size="small" />
          </Styled.CloseButton>
        </Styled.DrawerWrapper>
      </Target>
    </SlidingDrawer>
  )
}

export default Drawer
